import {QUESTION_TYPES} from 'client/constants';
import {List, Map} from 'immutable';

export const authoringQuestionModelValidators = {
  getDifficulty() {
    if (this.getDifficulty() === 0) {
      return List(['You must select a difficulty.']);
    }
    return null;
  },

  getDropdownText() {
    if (this.getQuestionType() !== QUESTION_TYPES.FILL_IN_THE_BLANK.name) {
      return null;
    }

    if (!this.getDropdownText() || this.getDropdownText().length === 0) {
      return List(['You must provide a dropdown prompt.']);
    }

    return null;
  },

  getDropdowns() {
    if (this.getQuestionType() !== QUESTION_TYPES.FILL_IN_THE_BLANK.name) {
      return null;
    }

    if (this.getDropdowns().isEmpty()) {
      return List(['You must create a dropdown.']);
    }

    return null;
  },

  getHeaderLeft() {
    if (this.getHeaderLeft() && this.getHeaderLeft().length === 0) {
      return List(['You must provide a left header.']);
    }

    return null;
  },

  getHeaderRight() {
    if (this.getHeaderRight() && this.getHeaderRight().length === 0) {
      return List(['You must provide a right header.']);
    }

    return null;
  },

  getInputs() {
    if (this.getQuestionType() !== QUESTION_TYPES.FREE_ENTRY.name) {
      return null;
    }

    if (this.getInputs().isEmpty()) {
      return List(['You must have at least one input prompt.']);
    }

    const inputsWithoutAnswers = this.getInputs().reduce((result, input) => {
      const correctAnswers = this.getValidResponse().get(input.get('id'));
      if (!correctAnswers || correctAnswers.isEmpty()) {
        return result.set(input.get('id'), 'You must provide a correct answer.');
      }
      return result;
    }, Map());

    if (!inputsWithoutAnswers.isEmpty()) {
      return List([inputsWithoutAnswers]);
    }

    return null;
  },

  getOptions() {
    const options = this.getOptions() || List();
    const optionsWithoutAnswers = options
      .filter((option) => !option.get('value'))
      .reduce((result, option) => {
        return result.set(option.get('id'), 'You must provide an answer.');
      }, new Map());
    if (!optionsWithoutAnswers.isEmpty()) {
      return List([optionsWithoutAnswers]);
    }

    const optionsWhiteList = List([
      QUESTION_TYPES.MULTIPLE_CHOICE.name,
      QUESTION_TYPES.SNIPPET_SELECTION.name
    ]);

    if (optionsWhiteList.includes(this.getQuestionType()) && options.isEmpty()) {
      return List(['You must provide an option.']);
    }

    return null;
  },

  getPreferredPermutation() {
    return null;
  },

  getPrompt() {
    if (this.getPrompt().length === 0) {
      return List(['You must provide a prompt.']);
    }
    return null;
  },

  getRows() {
    if (this.getQuestionType() !== QUESTION_TYPES.TWO_WAY.name) {
      return null;
    }

    if (this.getRows().isEmpty()) {
      return List(['At least one row is required.']);
    }

    return null;
  },

  getRubric() {
    if (this.getQuestionType() === QUESTION_TYPES.FREE_RESPONSE.name) {
      // FRQ criteria-rubric validation is intentionally NOT done here. The criteria
      // rubric is only editable behind the `frq_rubric_authoring_dennis` flag, so its
      // blocking validation lives in that flag-gated layer (the editor save handler).
      // Returning null preserves prior FRQ save behavior and guarantees a flag-off
      // author is never blocked by a criteria rubric they cannot see or edit.
      return null;
    }

    if (this.getQuestionType() === QUESTION_TYPES.GRAPH_CONTAINS.name) {
      if (!this.getValidResponse().size) {
        return List(['The question must have at least one correct answer element.']);
      }

      const hasAnswerWithNoCorrectAnswerSetting = this.getValidResponse().some((entity) => {
        return entity.get('correctAnswerSettings').every((value) => value === false);
      });

      if (hasAnswerWithNoCorrectAnswerSetting) {
        return List(['All correct answers must have at least one correct answer setting enabled.']);
      }
    }

    if (
      this.getValidResponse() &&
      this.getValidResponse().isEmpty() &&
      this.getQuestionType() !== QUESTION_TYPES.TWO_WAY.name &&
      this.getQuestionType() !== QUESTION_TYPES.ITEM_BANK.name &&
      this.getQuestionType() !== QUESTION_TYPES.ORDERING.name
    ) {
      return List(['A correct answer is required.']);
    }

    if (this.getQuestionType() === QUESTION_TYPES.ITEM_BANK.name) {
      const slots = this.getQuestionTypeAttributes().getIn(['item-bank', 'slots'], List());
      const validResponse = this.getValidResponse();
      if (slots.size !== validResponse.size) {
        return List(['The number of correct answers must match the number of slots.']);
      }
    }

    if (this.getQuestionType() === QUESTION_TYPES.ORDERING.name) {
      const items = this.getQuestionTypeAttributes().getIn(['ordering', 'items'], List());
      const slots = this.getQuestionTypeAttributes().getIn(['ordering', 'slots'], List());
      const validResponse = this.getValidResponse();

      if (!validResponse || validResponse.isEmpty() || validResponse.size < 2) {
        return List(['A correct answer is required.']);
      }

      if (slots.size !== validResponse.size) {
        return List(['The correct answer must assign all ordering slots.']);
      }

      const slotIds = slots.map((slot) => slot.get('id')).toSet();
      const hasInvalidSlotId = validResponse.keySeq().some((key) => !slotIds.has(key));
      if (hasInvalidSlotId) {
        return List(['The correct answer contains an invalid slot reference.']);
      }

      const hasEmptyItem = items.some(
        (item) => !item.get('value') || item.get('value').trim() === ''
      );
      if (hasEmptyItem) {
        return List(['All answer options must have a value.']);
      }
    }

    if (this.getQuestionType() === QUESTION_TYPES.FILL_IN_THE_BLANK.name) {
      const dropdownsWithoutAnswers = this.getDropdowns().reduce((result, dropdown) => {
        if (!this.getValidResponse().get(dropdown.get('id'))) {
          return result.set(dropdown.get('id'), 'You must select a correct answer.');
        }
        return result;
      }, Map());

      if (!dropdownsWithoutAnswers.isEmpty()) {
        return List([dropdownsWithoutAnswers]);
      }
    }

    return null;
  },

  getValidResponse() {
    return null;
  },

  getSnippetPrompt() {
    if (this.getQuestionType() !== QUESTION_TYPES.SNIPPET_SELECTION.name) {
      return null;
    }

    if (this.getSnippetPrompt().length === 0) {
      return List(['You must provide a snippet prompt.']);
    }

    return null;
  },

  getSolutionText() {
    if (!this.getSolutionText()) {
      return List(['You must provide an explanation of the solution.']);
    }
    return null;
  },

  getTitle() {
    if (this.getTitle().length === 0) {
      return List(['You must provide a title.']);
    }
    if (this.getTitle().length > 255) {
      return List(['Question titles must be 255 characters or less.']);
    }
    return null;
  }
};
